import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { formatDate } from '../formatDate';

interface Earning {
  id: number;
  orderId: number;
  amount: number;
  createdAt: string;
}

interface InvoiceSummary {
  id: number;
  number: string;
  kind: number;
  isCreditNote?: boolean;
}

interface UserEarningsResponse {
  totalEarned: number;
  earnings: Earning[];
}

export const EarningsPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const [data, setData] = useState<UserEarningsResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [rentalInvoices, setRentalInvoices] = useState<Record<number, InvoiceSummary>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<UserEarningsResponse>('/earnings/my');
      setData(res.data);

      const invoiceMap: Record<number, InvoiceSummary> = {};
      await Promise.all(res.data.earnings.map(async earning => {
        try {
          const invRes = await api.get<InvoiceSummary[]>(`/orders/${earning.orderId}/invoices`);
          const rental = invRes.data.find(i => i.kind === 1 && !i.isCreditNote);
          if (rental) invoiceMap[earning.orderId] = rental;
        } catch {
          // no invoices for this order yet
        }
      }));
      setRentalInvoices(invoiceMap);
    } catch (err) {
      setError(getErrorMessage(err, t('earnings.load_error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const downloadInvoice = async (invoiceId: number, fileName: string) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, t('invoice.download_error')));
    }
  };

  if (loading) {
    return <p className="text-muted">{t('earnings.loading')}</p>;
  }

  return (
    <div>
      <h2 className="section-title">{t('earnings.title')}</h2>

      {error && (
        <div className="alert alert-error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {data && (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <p className="order-meta">{t('earnings.total_earned', { amount: data.totalEarned })}</p>
              </div>
            </div>
          </div>

          {data.earnings.length === 0 && !error && (
            <p className="text-muted">{t('earnings.none')}</p>
          )}

          {data.earnings.map(e => (
            <div key={e.id} className="card" style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <p className="order-meta">
                    {t('earnings.order', { id: e.orderId })}&nbsp;&middot;&nbsp;
                    {formatDate(e.createdAt, lang)}
                  </p>
                  {rentalInvoices[e.orderId] && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: '0.35rem' }}
                      onClick={() => downloadInvoice(
                        rentalInvoices[e.orderId].id,
                        rentalInvoices[e.orderId].number
                      )}
                    >
                      {t('invoice.download_rental_short')}
                    </button>
                  )}
                </div>
                <div className="order-total">
                  {t('earnings.amount', { amount: e.amount })}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};
